import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, ExternalLink, Trash2 } from "lucide-react";
import { api, formatPrice } from "../lib/api";
import { useT } from "../lib/i18n";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const Reacties = () => {
  const { t } = useT();
  const STATUS = {
    gereageerd: { label: t("p2.st.gereageerd"), cls: "bg-brandblue-light text-brandblue" },
    uitgenodigd: { label: t("p2.st.uitgenodigd"), cls: "bg-success/15 text-success" },
    afgewezen: { label: t("p2.st.afgewezen"), cls: "bg-danger/15 text-danger" },
    ingetrokken: { label: t("p2.st.ingetrokken"), cls: "bg-slate-100 text-slate-500" },
  };
  const [items, setItems] = useState(null);

  const load = () => api("/responses").then((d) => setItems(d.responses)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api(`/responses/${id}/status`, { method: "PATCH", body: JSON.stringify({ status }) });
    load();
  };
  const remove = async (id) => { await api(`/responses/${id}`, { method: "DELETE" }); load(); };

  return (
    <div>
      <PageHeader icon={Inbox} title={t("p2.reacties.title")} subtitle={t("p2.reacties.subtitle")} />
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        {items === null ? (
          <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-100" />)}</div>
        ) : items.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-border p-16 text-center" data-testid="reacties-empty">
            <Inbox className="mx-auto text-slate-300" size={44} />
            <p className="mt-4 text-slate-500">{t("p2.reacties.empty")}</p>
            <Button asChild className="mt-6 rounded-xl bg-brandblue hover:bg-brandblue/90"><Link to="/woningen">{t("p2.reacties.viewHomes")}</Link></Button>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((r) => {
              const s = r.snapshot || {};
              const st = STATUS[r.status] || STATUS.gereageerd;
              return (
                <div key={r.id} data-testid={`reactie-${r.id}`} className="flex flex-wrap items-center gap-4 rounded-2xl border border-border bg-white p-4 shadow-sm">
                  <div className="h-16 w-20 shrink-0 overflow-hidden rounded-xl bg-slate-100">
                    {s.image && <img src={s.image} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-display text-base font-bold text-navy">{s.title || t("common.rooms")}</div>
                    <div className="truncate text-sm text-slate-500">{s.street}, {s.city} · {formatPrice(s.price)}</div>
                    <span className={`mt-1.5 inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${st.cls}`}>{st.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                      <SelectTrigger className="w-[150px] rounded-xl" data-testid={`reactie-status-${r.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {r.external_url && (
                      <Button asChild variant="outline" size="icon" className="rounded-full">
                        <a href={r.external_url} target="_blank" rel="noopener noreferrer"><ExternalLink size={16} /></a>
                      </Button>
                    )}
                    <Button variant="outline" size="icon" className="rounded-full text-danger" onClick={() => remove(r.id)} data-testid={`reactie-delete-${r.id}`}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reacties;
